import React from 'react'

import { fonts, CustomTheme } from '@masfront/common-b2c'

import { Box } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import HomeIcon from '@material-ui/icons/Home'

import useBrandStyles from './ProductStatusDetailBrandStyles'
import TriangleDivider from './TriangleDivider'

import {
  subscriptionStatusGroupText,
  subscriptionStatusText,
} from '~/customer/helpers/customer.helpers'
import { SubscriptionStatusGroup, SubscriptionStatus } from '~/customer/model/customer.model'
import B2CAlert from '~/ui/components/B2CAlert/B2CAlert'

type AlertVariant = 'big' | 'small' | 'chip' | 'rounded'

interface StylesProps {
  status?: SubscriptionStatus
  statusGroup?: SubscriptionStatusGroup
  variant?: AlertVariant
}

const useStyles = makeStyles((theme: CustomTheme) => {
  return createStyles({
    root: {
      '& .MuiAlert-root': {
        boxShadow: 'none',
      },
      '& .MuiSvgIcon-root': {
        fontSize: fonts.size.base,
        paddingRight: ({ status }: StylesProps) => !status && theme.spacing(2),
      },
      '& .MuiAlert-message': {
        padding: theme.spacing(0, 1, 0, 5),
        margin: 0,
      },
    },
    secondAlert: {
      paddingLeft: theme.spacing(3),
      marginLeft: '-1px',
      '& .MuiAlert-message': {
        color: theme.palette.text.primary,
      },
    },
    singleStatus: {
      minWidth: '100%',
      justifyContent: 'start',
      '& .MuiAlert-message': {
        padding: theme.spacing(0, 1, 0, 3),
      },
    },
    multipleStatus: {
      minWidth: 'max-content',
      justifyContent: 'space-around',
    },
    leftPosition: {
      minWidth: 'max-content',
      paddingLeft: theme.spacing(2),
      justifyContent: 'space-around',
      boxShadow: 'none',
      borderTopLeftRadius: ({ variant }: StylesProps) =>
        variant === 'rounded' ? '50%' : theme.spacing(0.5),
      borderTopRightRadius: ({ variant }: StylesProps) => (variant === 'rounded' ? '50%' : 0),
      borderBottomLeftRadius: ({ variant }: StylesProps) =>
        variant === 'rounded' ? '50%' : theme.spacing(0.5),
      borderBottomRightRadius: ({ variant }: StylesProps) => (variant === 'rounded' ? '50%' : 0),
      '& .MuiAlert-message': {
        padding: theme.spacing(0, 1, 0, 3),
        margin: 0,
      },
      '& .MuiAlert-icon': {
        padding: theme.spacing(0, 2, 0, 1),
      },
    },
    rightPosition: {
      paddingLeft: theme.spacing(3),
      minWidth: 'max-content',
      boxShadow: 'none',
      borderTopLeftRadius: ({ variant }: StylesProps) => (variant === 'rounded' ? '50%' : 0),
      borderTopRightRadius: ({ variant }: StylesProps) =>
        variant === 'rounded' ? '50%' : theme.spacing(0.5),
      borderBottomLeftRadius: ({ variant }: StylesProps) => (variant === 'rounded' ? '50%' : 0),
      borderBottomRightRadius: ({ variant }: StylesProps) =>
        variant === 'rounded' ? '50%' : theme.spacing(0.5),
      '& .MuiAlert-message': {
        textTransform: 'inherit',
        margin: 0,
        padding: theme.spacing(0, 4, 0, 3),
        '& .MuiTypography-h6': {
          fontWeight: 'normal',
        },
      },
    },
  })
})

interface ProductStatusDetailProps {
  statusGroup: SubscriptionStatusGroup
  status?: SubscriptionStatus
}

const ProductStatusDetail: React.FC<ProductStatusDetailProps> = ({ status, statusGroup }) => {
  const classes = useStyles({ statusGroup, status })
  const brandClasses = useBrandStyles({ status, statusGroup })

  const icon = <HomeIcon />
  const firstAlertText = subscriptionStatusGroupText[statusGroup].text
  const secondAlertText = status ? subscriptionStatusText[status].text : ''

  return (
    <Box className={`${classes.root} ${brandClasses.icon}`} display="flex">
      {statusGroup && (
        <Box className={status ? classes.multipleStatus : classes.singleStatus}>
          <B2CAlert
            className={`${brandClasses.firstAlert} ${
              status ? classes.leftPosition : classes.singleStatus
            }`}
            icon={icon}
            alertText={firstAlertText}
            variant="big"
          />
        </Box>
      )}
      {status && (
        <>
          <TriangleDivider statusGroup={statusGroup} />
          <Box width="100%">
            <B2CAlert
              className={`${classes.secondAlert} ${brandClasses.secondAlert} ${classes.rightPosition}`}
              alertText={secondAlertText}
              variant="big"
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default ProductStatusDetail
